import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { StationTally } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, Modal, PageHeader, StatCard, StatusBadge } from '../../ui/components';

const VERIFY_ROLES = ['RETURNING_OFFICER', 'ELECTORAL_COMMISSIONER'];
const SUBMIT_ROLES = ['RETURNING_OFFICER'];

/** GET /tallies/:id — candidate vote breakdown + verify/dispute/resubmit. */
export function TallyDetail() {
  const { id } = useParams<{ id: string }>();
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const canVerify = roles.some((r) => VERIFY_ROLES.includes(r));
  const canResubmit = roles.some((r) => SUBMIT_ROLES.includes(r));
  const isCommissioner = roles.includes('ELECTORAL_COMMISSIONER');
  const tallyQ = useApiQuery<StationTally>(id ? `/api/v1/m39/tallies/${id}` : null);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showDispute, setShowDispute] = useState(false);
  const [showResubmit, setShowResubmit] = useState(false);

  if (tallyQ.loading) return <div className="px-4 py-8 text-sm text-slate-500">Loading tally…</div>;
  if (tallyQ.error) return <ErrorNote message={tallyQ.error} />;
  const t = tallyQ.data;
  if (!t) return null;

  const verify = async () => {
    setBusy('verify');
    setActionError(null);
    try {
      await api.post(`/api/v1/m39/tallies/${t.id}/verify`);
      tallyQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Verification failed');
    } finally {
      setBusy(null);
    }
  };

  const canDispute = t.status === 'SUBMITTED' || (t.status === 'VERIFIED' && isCommissioner);

  return (
    <div>
      <PageHeader title={`Station tally — ${t.pollingStationId}`} subtitle={`Election ${t.electionId}`} actions={<StatusBadge status={t.status} />} />

      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Registered voters" value={t.registeredVoters} />
        <StatCard label="Ballots issued" value={t.ballotsIssued} />
        <StatCard label="Valid votes" value={t.validVotes} tone="good" />
        <StatCard label="Rejected votes" value={t.rejectedVotes} tone={t.rejectedVotes > 0 ? 'warn' : 'default'} />
      </div>

      <div className="card mb-6 p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Candidate vote breakdown</h2>
        {(t.candidateVotes?.length ?? 0) === 0 ? (
          <p className="text-xs text-slate-400">No per-candidate rows recorded.</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Nomination id</th>
                <th className="table-head text-right">Votes</th>
              </tr>
            </thead>
            <tbody>
              {t.candidateVotes!.map((cv) => (
                <tr key={cv.id}>
                  <td className="table-cell font-mono text-xs">{cv.nominationId}</td>
                  <td className="table-cell text-right tabular-nums">{cv.votes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card mb-6 grid grid-cols-2 gap-3 p-5 text-sm sm:grid-cols-4">
        <div>
          <div className="field-label">Submitted by</div>
          {t.submittedBy ?? '—'}
        </div>
        <div>
          <div className="field-label">Submitted at</div>
          {formatDateTime(t.submittedAt)}
        </div>
        <div>
          <div className="field-label">Verified by</div>
          {t.verifiedBy ?? '—'}
        </div>
        <div>
          <div className="field-label">Verified at</div>
          {formatDateTime(t.verifiedAt)}
        </div>
      </div>

      {t.disputeReason ? <div className="card mb-6 border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">Disputed: {t.disputeReason}</div> : null}

      <div className="flex flex-wrap gap-2">
        {canVerify && t.status === 'SUBMITTED' ? (
          <button className="btn-success" onClick={verify} disabled={busy !== null}>
            {busy === 'verify' ? 'Verifying…' : 'Verify (four-eyes)'}
          </button>
        ) : null}
        {canVerify && canDispute ? (
          <button className="btn-danger" onClick={() => setShowDispute(true)} disabled={busy !== null}>
            Dispute
          </button>
        ) : null}
        {canResubmit && t.status === 'DISPUTED' ? (
          <button className="btn-primary" onClick={() => setShowResubmit(true)} disabled={busy !== null}>
            Resubmit corrected tally
          </button>
        ) : null}
      </div>
      {t.status === 'SUBMITTED' ? (
        <p className="mt-2 text-xs text-slate-400">
          Dual control: the officer who submitted this tally cannot also verify it — a different {VERIFY_ROLES.join('/')} must sign in.
        </p>
      ) : null}

      {showDispute ? (
        <DisputeModal
          tallyId={t.id}
          onClose={() => setShowDispute(false)}
          onDone={() => {
            setShowDispute(false);
            tallyQ.reload();
          }}
        />
      ) : null}
      {showResubmit ? (
        <ResubmitModal
          tally={t}
          onClose={() => setShowResubmit(false)}
          onDone={() => {
            setShowResubmit(false);
            tallyQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function DisputeModal({ tallyId, onClose, onDone }: { tallyId: string; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m39/tallies/${tallyId}/dispute`, { reason });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Dispute failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Dispute this tally" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Reason">
          <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Ballot accounting mismatch found on recount" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-danger" onClick={submit} disabled={busy || reason.trim().length < 3}>
            {busy ? 'Disputing…' : 'Dispute'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ResubmitModal({ tally, onClose, onDone }: { tally: StationTally; onClose: () => void; onDone: () => void }) {
  const [registeredVoters, setRegisteredVoters] = useState(String(tally.registeredVoters));
  const [ballotsIssued, setBallotsIssued] = useState(String(tally.ballotsIssued));
  const [validVotes, setValidVotes] = useState(String(tally.validVotes));
  const [rejectedVotes, setRejectedVotes] = useState(String(tally.rejectedVotes));
  const [candidateVotesText, setCandidateVotesText] = useState((tally.candidateVotes ?? []).map((cv) => `${cv.nominationId},${cv.votes}`).join('\n'));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const candidateVotes = candidateVotesText
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const [nominationId, votes] = line.split(',').map((s) => s.trim());
          return { nominationId, votes: Number.parseInt(votes ?? '0', 10) };
        });
      await api.post(`/api/v1/m39/tallies/${tally.id}/resubmit`, {
        registeredVoters: Number.parseInt(registeredVoters, 10),
        ballotsIssued: Number.parseInt(ballotsIssued, 10),
        validVotes: Number.parseInt(validVotes, 10),
        rejectedVotes: Number.parseInt(rejectedVotes, 10),
        candidateVotes,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Resubmission failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Resubmit corrected tally" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-4 gap-3">
          <Field label="Registered voters">
            <input type="number" className="field-input" value={registeredVoters} onChange={(e) => setRegisteredVoters(e.target.value)} />
          </Field>
          <Field label="Ballots issued">
            <input type="number" className="field-input" value={ballotsIssued} onChange={(e) => setBallotsIssued(e.target.value)} />
          </Field>
          <Field label="Valid votes">
            <input type="number" className="field-input" value={validVotes} onChange={(e) => setValidVotes(e.target.value)} />
          </Field>
          <Field label="Rejected votes">
            <input type="number" className="field-input" value={rejectedVotes} onChange={(e) => setRejectedVotes(e.target.value)} />
          </Field>
        </div>
        <Field label="Candidate votes — one “nominationId,votes” pair per line">
          <textarea className="field-input font-mono text-xs" rows={4} value={candidateVotesText} onChange={(e) => setCandidateVotesText(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Resubmitting…' : 'Resubmit'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
